import { useEffect, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from '@remix-run/react';

import { LocalStorageKeys } from '~/interfaces/local_storage';
import type { Publication } from '~/interfaces/publication';

const useSignupRedirects = (publication: Publication) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [redirects, setRedirects] = useState<any>();
  const [searchParams] = useSearchParams();
  const redirectLength = redirects ? redirects?.redirects?.length : 0;
  const stepCount = redirects ? redirects?.currentIndex + 1 : 0;
  const progress = (stepCount / redirectLength) * 100;

  const clearRedirects = () => {
    localStorage.removeItem(LocalStorageKeys.SIGNUP_REDIRECTS);
  };

  useEffect(() => {
    const redirectsInProcess = localStorage?.getItem(LocalStorageKeys.SIGNUP_REDIRECTS);

    if (redirectsInProcess) {
      const parsedRedirects = JSON.parse(redirectsInProcess);
      const startedAt = parsedRedirects.startedAt;
      const startedAtTime = new Date(startedAt).getTime();
      const now = new Date().getTime();
      const timeSinceStarted = now - startedAtTime;
      const timeLimit = 1000 * 60 * 3; // 3 minutes

      // If the user has been in the middle of the signup flow for more than 3 minutes, remove the redirects from local storage
      // This might happen if the user leaves the page open or comes back
      if (timeSinceStarted > timeLimit) {
        clearRedirects();
      } else {
        setRedirects(JSON.parse(redirectsInProcess));
      }
    } else {
      redirects && setRedirects(undefined);
    }
  }, [location]);

  // When a signup flow starts, we append a banner to the top of the page.
  // Occasionally, users exit the flow by switching the url manually and the banner persists
  // This checks if the url is different than the current step and if so, removes the redirects from local storage
  useEffect(() => {
    if (redirects) {
      const hrefs = redirects.redirects.map((r: any) => r.href);
      const pathname = location.pathname;
      const isOnWrongPage = !hrefs.includes(pathname);
      const isRecommendationsStep = searchParams.get('recommendations') === 'true';

      if (isOnWrongPage && !isRecommendationsStep) {
        clearRedirects();
        setRedirects(undefined);
      }
    }
  }, [redirects]);

  return {
    currentStep: redirects?.redirects?.[redirects?.currentIndex],
    nextStep: redirects?.redirects?.[redirects?.nextIndex],
    isInProgress: Boolean(redirects),
    redirects: redirects?.redirects,
    progress: Number(progress.toFixed(1)),
    nextPage: () => {
      // Scenario 1: Not in the middle of a signup flow, so just redirect to the homepage
      const hasCustomRedirects = Boolean(redirects);
      if (!hasCustomRedirects) {
        return navigate('/');
      }

      // Scenario 2: If this is an external upgrade page, redirect to it and remove the redirects from local storage
      const nextStep = redirects?.redirects?.[redirects?.currentIndex + 1];
      const isNextPageUpgrade = nextStep?.managed_type === 'upgrade';
      const upgradeHref = publication.web_template.external_upgrade_page_href;

      if (isNextPageUpgrade && Boolean(upgradeHref)) {
        window.open(upgradeHref, '_blank');
        clearRedirects();
        return navigate('/');
      }

      // Scenario 3: If this is the last redirect, remove the redirects from local storage and redirect to the homepage
      const lastIndex = redirects.redirects.length - 1;

      if (redirects.nextIndex > lastIndex) {
        clearRedirects();
        navigate('/');
      } else {
        const stringifiedEmail = localStorage.getItem(LocalStorageKeys.EMAIL);
        const email = stringifiedEmail && JSON.parse(stringifiedEmail);
        const nextRedirect = redirects.redirects[redirects.nextIndex];
        const isNextRedirectCustom = nextRedirect.managed_type === 'custom';
        const isNextRedirectExternal = !nextRedirect.full_url.startsWith(publication.url);

        // Scenario 4: There is a custom redirect, but it's external, so open it in a new tab and redirect to the homepage
        if (isNextRedirectCustom && isNextRedirectExternal) {
          try {
            const externalRedirectUrl = nextRedirect.href;
            const url = new URL(externalRedirectUrl);
            url.searchParams.set('email', email);
            window.open(url.href, '_blank');
            clearRedirects();
            navigate('/');
          } catch {
            clearRedirects();
            navigate('/');
          }

          // Scenario 5: There is a custom redirect, but it's internal, so redirect to it, but remove the redirects from local storage and finish the flow
        } else if (isNextRedirectCustom) {
          clearRedirects();
          navigate(nextRedirect.href);

          // Scenario 6: We are in the middle of the signup flow, so redirect to the next page and update the local storage
        } else {
          const nextUrl = `${nextRedirect.href}?email=${email}`;
          const redirectState = {
            currentIndex: redirects.currentIndex + 1,
            nextIndex: redirects.nextIndex + 1,
            redirects: redirects.redirects,
          };

          localStorage.setItem(LocalStorageKeys.SIGNUP_REDIRECTS, JSON.stringify(redirectState));

          navigate(nextUrl);
        }
      }
    },
  };
};

export default useSignupRedirects;
